import { Container, Stack, Switch, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";
import { AppClipTabContainer_brand$key } from "./__generated__/AppClipTabContainer_brand.graphql";
import CardSection from "../../../components/CardSection";
import LeftRight from "../../../components/LeftRight";
import SaveButton, { SavedState } from "../../../components/SaveButton";
import TabContainer, { TabConfig } from "../../../components/TabContainer";
import useBrandAppConfigUpdateMutation from "../../../mutations/useBrandAppConfigUpdateMutation";
import AppClipDefaultCard from "./app-clip/AppClipDefaultCard";
import AppClipNotifications from "./app-clip/AppClipNotifications";

const fragment = graphql`
  fragment AppClipTabContainer_brand on BrandType {
    id
    appConfig {
      id
      iosAppClipsEnabled
    }
  }
`;

const appClipConfigs: TabConfig[] = [
  {
    title: "Push Notifications",
    pathname: "push-notifications",
    element(brandID) {
      return <AppClipNotifications brandID={brandID} key={brandID} />;
    },
  },
  {
    title: "Default Card",
    pathname: "default-card",
    element(brandID) {
      return <AppClipDefaultCard brandID={brandID} key={brandID} />;
    },
  },
];

const AppClipTabContainer = ({
  brand: brandKey,
}: {
  brand: AppClipTabContainer_brand$key;
}) => {
  const brand = useFragment<AppClipTabContainer_brand$key>(fragment, brandKey);
  const [isAppClipEnabled, setIsAppClipEnabled] = useState(
    brand.appConfig.iosAppClipsEnabled
  );
  const [saveButtonState, setSaveButtonState] = useState(SavedState.SAVED);

  const [saveMutation, isMutationInFlight] = useBrandAppConfigUpdateMutation(
    {
      id: brand.appConfig.id,
      iosAppClipsEnabled: isAppClipEnabled,
    },
    () => {
      setSaveButtonState(SavedState.SAVED);
    }
  );

  return (
    <Container maxWidth="md">
      <Stack spacing={2}>
        <CardSection
          title="App Clips"
          subtitle="Configure App Clips settings"
          actions={
            <SaveButton
              savedState={
                isMutationInFlight ? SavedState.DISABLED : saveButtonState
              }
              onClick={saveMutation}
            />
          }
          content={
            <Stack spacing={2}>
              <LeftRight
                expandLeft={true}
                left={
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      Enable App Clips
                    </Typography>
                    <Typography variant="body2">
                      App Clips allow iOS users to instantly access parts of
                      your app without installing it, letting customers quickly
                      view products and make purchases without downloading the
                      full app.
                    </Typography>
                  </Stack>
                }
                right={
                  <Switch
                    checked={isAppClipEnabled}
                    onChange={(e) => {
                      setIsAppClipEnabled(e.target.checked);
                      if (
                        e.target.checked !== brand.appConfig.iosAppClipsEnabled
                      ) {
                        setSaveButtonState(SavedState.ENABLED);
                      } else {
                        setSaveButtonState(SavedState.SAVED);
                      }
                    }}
                  />
                }
              />
            </Stack>
          }
        />
        <TabContainer brandID={brand.id} tabs={appClipConfigs} />
      </Stack>
    </Container>
  );
};

export default AppClipTabContainer;
